import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { PageHeader } from '@/components/layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ArrowLeft,
  Pencil,
  Download,
  Copy,
  Trash2,
  ExternalLink,
  QrCode,
  BarChart3,
  Settings,
  MoreHorizontal,
  Play,
  Pause,
  Archive,
  Check,
  Link2,
  Calendar,
  Tag,
  Globe,
  MapPin,
  Building2,
  Users,
  Handshake,
  Home,
  Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiV1 } from '@/api/client';
import QRCodeStyling from 'qr-code-styling';
import PrintReadyQRDialog from '@/components/PrintReadyQRDialog';
import { makeSvgPortable } from '@/lib/qrSvgExport';

export default function QRDetail() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [qrCode, setQRCode] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    destination_url: '',
    tracking: {
      region_id: '',
      region_name: '',
      office_id: '',
      office_name: '',
      agent_id: '',
      agent_name: '',
      partnership_id: '',
      partnership_name: '',
      listing_ref: '',
    },
    utm: {
      utm_campaign: '',
    },
  });
  const [saving, setSaving] = useState(false);
  const [referenceData, setReferenceData] = useState({
    regions: [],
    offices: [],
    agents: [],
    partnerships: [],
  });
  
  // QR code preview ref
  const qrPreviewRef = useRef(null);
  const qrCodeInstanceRef = useRef(null);

  useEffect(() => {
    fetchQRCode();
    fetchReferenceData();
  }, [id]);

  // Auto-open the edit dialog when the URL carries ?edit=1 (e.g., from the
  // list dropdown "Edit details" action). Only fires once the QR has loaded so
  // the form starts pre-populated. Strips the flag after opening so a manual
  // dialog close doesn't leave the URL primed to reopen.
  useEffect(() => {
    if (!qrCode) return;
    if (searchParams.get('edit') === '1') {
      setEditDialogOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete('edit');
      setSearchParams(next, { replace: true });
    }
  }, [qrCode, searchParams, setSearchParams]);

  // Fetch reference data for dropdowns
  const fetchReferenceData = async () => {
    try {
      const response = await apiV1.get('/reference-data/options');
      if (response.data.success) {
        setReferenceData({
          regions: response.data.data.region || [],
          offices: response.data.data.office || [],
          agents: response.data.data.agent || [],
          partnerships: response.data.data.partnership || [],
        });
      }
    } catch (error) {
      console.error('Failed to load reference data:', error);
    }
  };

  // Generate QR code preview
  useEffect(() => {
    if (qrCode && qrPreviewRef.current) {
      const design = qrCode.design || {};
      
      // Use qr_url (tracking URL) so scans are tracked, not the destination directly
      const qrOptions = {
        width: 250,
        height: 250,
        data: qrCode.qr_url || qrCode.tracked_url || qrCode.destination_url,
        dotsOptions: {
          type: design.dots_options?.type || 'square',
          color: design.dots_options?.color || '#000000',
        },
        cornersSquareOptions: {
          type: design.corners_square_options?.type || 'square',
          color: design.corners_square_options?.color || '#000000',
        },
        cornersDotOptions: {
          type: design.corners_dot_options?.type || 'square',
          color: design.corners_dot_options?.color || '#000000',
        },
        backgroundOptions: {
          color: design.background_options?.color || '#ffffff',
        },
        imageOptions: {
          crossOrigin: 'anonymous',
          margin: design.image_options?.margin || 5,
          imageSize: design.image_options?.image_size || 0.3,
          hideBackgroundDots: design.image_options?.hide_background_dots ?? true,
        },
      };

      // Set image/logo from image_options.image_url
      if (design.image_options?.image_url) {
        qrOptions.image = design.image_options.image_url;
      }

      if (qrPreviewRef.current) {
        qrPreviewRef.current.innerHTML = '';
      }

      const qrCodeInstance = new QRCodeStyling(qrOptions);
      qrCodeInstanceRef.current = qrCodeInstance;
      qrCodeInstance.append(qrPreviewRef.current);
    }
  }, [qrCode]);

  const fetchQRCode = async () => {
    setLoading(true);
    try {
      const response = await apiV1.get(`/qrcodes/${id}`);
      if (response.data.success) {
        const data = response.data.data;
        setQRCode(data);
        setEditForm({
          name: data.name || '',
          description: data.description || '',
          destination_url: data.destination_url || '',
          tracking: {
            region_id: data.tracking?.region_id || '',
            region_name: data.tracking?.region_name || '',
            office_id: data.tracking?.office_id || '',
            office_name: data.tracking?.office_name || '',
            agent_id: data.tracking?.agent_id || '',
            agent_name: data.tracking?.agent_name || '',
            partnership_id: data.tracking?.partnership_id || '',
            partnership_name: data.tracking?.partnership_name || '',
            listing_ref: data.tracking?.listing_ref || '',
          },
          utm: {
            utm_campaign: data.utm?.utm_campaign || '',
          },
        });
      }
    } catch (error) {
      console.error('Error fetching QR code:', error);
      toast.error('Failed to load QR code');
      navigate('/qrs');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await apiV1.put(`/qrcodes/${id}`, { status: newStatus });
      toast.success(`QR code ${newStatus === 'active' ? 'activated' : newStatus === 'paused' ? 'paused' : 'updated'}`);
      fetchQRCode();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to archive this QR code?')) return;
    
    try {
      await apiV1.delete(`/qrcodes/${id}`);
      toast.success('QR code archived');
      navigate('/qrs');
    } catch (error) {
      toast.error('Failed to delete QR code');
    }
  };

  const handleDuplicate = async () => {
    try {
      const response = await apiV1.post(`/qrcodes/${id}/duplicate`);
      if (response.data.success) {
        toast.success('QR code duplicated');
        navigate(`/qrs/${response.data.data.id}`);
      }
    } catch (error) {
      toast.error('Failed to duplicate QR code');
    }
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const payload = {
        name: editForm.name,
        description: editForm.description || null,
      };
      
      // Every QR code encodes its /r/ tracking link, so the destination can
      // be changed for any code (the old "static" codes included).
      if (editForm.destination_url && editForm.destination_url !== qrCode.destination_url) {
        payload.destination_url = editForm.destination_url;
      }
      
      // Include tracking parameters
      payload.tracking = {
        region_id: editForm.tracking.region_id || null,
        region_name: editForm.tracking.region_name || null,
        office_id: editForm.tracking.office_id || null,
        office_name: editForm.tracking.office_name || null,
        agent_id: editForm.tracking.agent_id || null,
        agent_name: editForm.tracking.agent_name || null,
        partnership_id: editForm.tracking.partnership_id || null,
        partnership_name: editForm.tracking.partnership_name || null,
        listing_ref: editForm.tracking.listing_ref || null,
      };
      
      // Include UTM parameters
      payload.utm = {
        utm_source: qrCode.utm?.utm_source || 'qr',
        utm_medium: qrCode.utm?.utm_medium || 'offline',
        utm_campaign: editForm.utm.utm_campaign || null,
      };
      
      await apiV1.put(`/qrcodes/${id}`, payload);
      toast.success('QR code updated');
      setEditDialogOpen(false);
      fetchQRCode();
    } catch (error) {
      console.error('Failed to update QR code:', error);
      toast.error('Failed to update QR code');
    } finally {
      setSaving(false);
    }
  };

  // Helper function to update tracking fields
  const updateTrackingField = (field, value, options, nameField) => {
    const selected = options?.find(o => o.id === value);
    setEditForm(prev => ({
      ...prev,
      tracking: {
        ...prev.tracking,
        [field]: value,
        [nameField]: selected?.name || '',
      }
    }));
  };

  const handleDownload = async (format = 'png') => {
    if (!qrCodeInstanceRef.current) return;
    const name = qrCode?.name || 'qr-code';
    if (format !== 'svg') {
      qrCodeInstanceRef.current.download({ name, extension: format });
      toast.success(`QR code downloaded as ${format.toUpperCase()}`);
      return;
    }
    // SVG goes through makeSvgPortable so the logo survives in design apps.
    try {
      const raw = await qrCodeInstanceRef.current.getRawData('svg');
      const svg = await makeSvgPortable(await raw.text());
      const url = window.URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${name}.svg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('QR code downloaded as SVG');
    } catch (error) {
      console.error('SVG download failed:', error);
      toast.error('Failed to download SVG');
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const getStatusBadge = (status) => {
    const config = {
      active: { variant: 'default', label: 'Active' },
      paused: { variant: 'secondary', label: 'Paused' },
      archived: { variant: 'outline', label: 'Archived' },
      expired: { variant: 'destructive', label: 'Expired' },
    };
    const { variant, label } = config[status] || { variant: 'outline', label: status };
    return <Badge variant={variant}>{label}</Badge>;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10 rounded" />
          <div className="space-y-2">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-32" />
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Skeleton className="h-96 col-span-2" />
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  if (!qrCode) {
    return (
      <div className="text-center py-12">
        <QrCode className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <h2 className="text-lg font-medium">QR Code not found</h2>
        <p className="text-muted-foreground mb-4">The QR code you're looking for doesn't exist.</p>
        <Button asChild>
          <Link to="/qrs">Back to QR Codes</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="qr-detail-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/qrs">
              <ArrowLeft className="h-5 w-5" />
            </Link>
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold">{qrCode.name}</h1>
              {getStatusBadge(qrCode.status)}
            </div>
            <p className="text-muted-foreground text-sm mt-1">
              Short code: <code className="bg-muted px-1 rounded">{qrCode.short_code}</code>
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" asChild>
            <Link to={`/editor?qr=${id}`}>
              <Pencil className="h-4 w-4 mr-2" />
              Edit Design
            </Link>
          </Button>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                <Download className="h-4 w-4 mr-2" />
                Download
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => handleDownload('png')}>
                Download PNG
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleDownload('svg')}>
                Download SVG
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleDownload('jpeg')}>
                Download JPEG
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setPrintDialogOpen(true)} data-testid="download-print-ready">
                Print-ready (CMYK TIFF)…
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreHorizontal className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setEditDialogOpen(true)}>
                <Pencil className="h-4 w-4 mr-2" />
                Edit Details
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleDuplicate}>
                <Copy className="h-4 w-4 mr-2" />
                Duplicate
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {qrCode.status === 'active' && (
                <DropdownMenuItem onClick={() => handleStatusChange('paused')}>
                  <Pause className="h-4 w-4 mr-2" />
                  Pause
                </DropdownMenuItem>
              )}
              {qrCode.status === 'paused' && (
                <DropdownMenuItem onClick={() => handleStatusChange('active')}>
                  <Play className="h-4 w-4 mr-2" />
                  Activate
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => handleStatusChange('archived')}>
                <Archive className="h-4 w-4 mr-2" />
                Archive
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-destructive" onClick={handleDelete}>
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          <Tabs defaultValue="details" className="w-full">
            <TabsList>
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="analytics">Analytics</TabsTrigger>
              <TabsTrigger value="tracking">Tracking</TabsTrigger>
            </TabsList>
            
            <TabsContent value="details" className="space-y-4 mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Destination</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-sm text-muted-foreground">Target URL</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <code className="flex-1 text-sm bg-muted p-2 rounded truncate">
                        {qrCode.destination_url}
                      </code>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => copyToClipboard(qrCode.destination_url, 'URL')}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" asChild>
                        <a href={qrCode.destination_url} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </Button>
                    </div>
                  </div>
                  
                  {qrCode.tracked_url && (
                    <div>
                      <Label className="text-sm text-muted-foreground">Tracked URL (with UTM parameters)</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <code className="flex-1 text-xs bg-muted p-2 rounded break-all">
                          {qrCode.tracked_url}
                        </code>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => copyToClipboard(qrCode.tracked_url, 'Tracked URL')}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {qrCode.qr_url && (
                    <div>
                      <Label className="text-sm text-muted-foreground">QR Redirect URL</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <code className="flex-1 text-sm bg-muted p-2 rounded truncate">
                          {qrCode.qr_url}
                        </code>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => copyToClipboard(qrCode.qr_url, 'QR URL')}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm text-muted-foreground">Created</Label>
                      <p className="text-sm font-medium mt-1">
                        {new Date(qrCode.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm text-muted-foreground">Last Updated</Label>
                      <p className="text-sm font-medium mt-1">
                        {new Date(qrCode.updated_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm text-muted-foreground">Type</Label>
                      <p className="text-sm font-medium mt-1">Tracked redirect</p>
                    </div>
                    <div>
                      <Label className="text-sm text-muted-foreground">Content Type</Label>
                      <p className="text-sm font-medium mt-1 capitalize">{qrCode.qr_type || 'URL'}</p>
                    </div>
                  </div>
                  
                  {qrCode.tags && qrCode.tags.length > 0 && (
                    <div className="mt-4">
                      <Label className="text-sm text-muted-foreground">Tags</Label>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {qrCode.tags.map((tag, i) => (
                          <Badge key={i} variant="secondary">{tag}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {qrCode.description && (
                    <div className="mt-4">
                      <Label className="text-sm text-muted-foreground">Description</Label>
                      <p className="text-sm mt-1">{qrCode.description}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="analytics" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Scan Statistics</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-muted/30 rounded-lg">
                      <p className="text-3xl font-bold">{qrCode.total_scans || 0}</p>
                      <p className="text-sm text-muted-foreground">Total Scans</p>
                    </div>
                    <div className="text-center p-4 bg-muted/30 rounded-lg">
                      <p className="text-3xl font-bold">{qrCode.unique_scans || 0}</p>
                      <p className="text-sm text-muted-foreground">Unique Scans</p>
                    </div>
                    <div className="text-center p-4 bg-muted/30 rounded-lg">
                      <p className="text-3xl font-bold">
                        {qrCode.last_scan_at
                          ? new Date(qrCode.last_scan_at).toLocaleDateString()
                          : 'Never'}
                      </p>
                      <p className="text-sm text-muted-foreground">Last Scan</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="tracking" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Tracking Parameters</CardTitle>
                  <CardDescription>
                    Attribution data for analytics filtering
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {qrCode.tracking && Object.values(qrCode.tracking).some(v => v) ? (
                    <div className="grid grid-cols-2 gap-4">
                      {qrCode.tracking.region_name && (
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="text-xs text-muted-foreground">Region</p>
                            <p className="text-sm font-medium">{qrCode.tracking.region_name}</p>
                          </div>
                        </div>
                      )}
                      {qrCode.tracking.office_name && (
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="text-xs text-muted-foreground">Office</p>
                            <p className="text-sm font-medium">{qrCode.tracking.office_name}</p>
                          </div>
                        </div>
                      )}
                      {qrCode.tracking.agent_name && (
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="text-xs text-muted-foreground">Agent</p>
                            <p className="text-sm font-medium">{qrCode.tracking.agent_name}</p>
                          </div>
                        </div>
                      )}
                      {qrCode.tracking.partnership_name && (
                        <div className="flex items-center gap-2">
                          <Handshake className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="text-xs text-muted-foreground">Partnership</p>
                            <p className="text-sm font-medium">{qrCode.tracking.partnership_name}</p>
                          </div>
                        </div>
                      )}
                      {qrCode.tracking.listing_ref && (
                        <div className="flex items-center gap-2">
                          <Tag className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="text-xs text-muted-foreground">Listing Ref</p>
                            <p className="text-sm font-medium">{qrCode.tracking.listing_ref}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No tracking parameters set</p>
                  )}
                  
                  {qrCode.utm && (
                    <div className="mt-4 pt-4 border-t">
                      <p className="text-sm font-medium mb-2">UTM Parameters</p>
                      <div className="flex flex-wrap gap-2">
                        {qrCode.utm.utm_source && (
                          <Badge variant="outline">source={qrCode.utm.utm_source}</Badge>
                        )}
                        {qrCode.utm.utm_medium && (
                          <Badge variant="outline">medium={qrCode.utm.utm_medium}</Badge>
                        )}
                        {qrCode.utm.utm_campaign && (
                          <Badge variant="outline">campaign={qrCode.utm.utm_campaign}</Badge>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* QR Code Preview Sidebar */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">QR Code Preview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-center p-6 bg-white rounded-lg border">
                <div ref={qrPreviewRef} className="flex items-center justify-center" />
              </div>
              <p className="text-xs text-muted-foreground text-center mt-3">
                Scan to test the destination
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button className="w-full justify-start" variant="outline" asChild>
                <Link to={`/editor?qr=${id}`}>
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit Design
                </Link>
              </Button>
              <Button className="w-full justify-start" variant="outline" onClick={() => handleDownload('png')}>
                <Download className="h-4 w-4 mr-2" />
                Download PNG
              </Button>
              <Button className="w-full justify-start" variant="outline" onClick={handleDuplicate}>
                <Copy className="h-4 w-4 mr-2" />
                Duplicate
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Edit QR Code</DialogTitle>
            <DialogDescription>
              Update the details, destination, and tracking parameters for this QR code.
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh] pr-4">
            <div className="space-y-6 py-4">
              {/* Basic Info */}
              <div className="space-y-4">
                <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Basic Information</h4>
                <div className="space-y-2">
                  <Label htmlFor="edit-name">Name *</Label>
                  <Input
                    id="edit-name"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    data-testid="qr-edit-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-description">Description</Label>
                  <Textarea
                    id="edit-description"
                    value={editForm.description}
                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                    rows={2}
                    data-testid="qr-edit-description-input"
                  />
                </div>
              </div>

              <Separator />

              {/* Destination URL */}
              <div className="space-y-4">
                <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Destination</h4>
                
                <div className="space-y-2">
                  <Label htmlFor="edit-url" className="flex items-center gap-2">
                    <Zap className="h-4 w-4 text-green-500" />
                    Destination URL
                  </Label>
                  <Input
                    id="edit-url"
                    type="url"
                    value={editForm.destination_url}
                    onChange={(e) => setEditForm({ ...editForm, destination_url: e.target.value })}
                    data-testid="qr-edit-url-input"
                  />
                  <p className="text-xs text-green-600">
                    The QR code points at its LinkHub link, so you can change the destination without reprinting. Scans of printed codes go to the new destination straight away.
                  </p>
                </div>
              </div>

              <Separator />

              {/* Tracking Parameters */}
              <div className="space-y-4">
                <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Tracking Parameters</h4>
                
                <div className="grid grid-cols-2 gap-4">
                  {/* Region */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      Region
                    </Label>
                    <Select
                      value={editForm.tracking.region_id || "none"}
                      onValueChange={(value) => updateTrackingField('region_id', value === "none" ? '' : value, referenceData.regions, 'region_name')}
                    >
                      <SelectTrigger data-testid="qr-edit-region-select">
                        <SelectValue placeholder="Select region" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {referenceData.regions.map(region => (
                          <SelectItem key={region.id} value={region.id}>
                            {region.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Office */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      Office
                    </Label>
                    <Select
                      value={editForm.tracking.office_id || "none"}
                      onValueChange={(value) => updateTrackingField('office_id', value === "none" ? '' : value, referenceData.offices, 'office_name')}
                    >
                      <SelectTrigger data-testid="qr-edit-office-select">
                        <SelectValue placeholder="Select office" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {referenceData.offices.map(office => (
                          <SelectItem key={office.id} value={office.id}>
                            {office.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Agent */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-muted-foreground" />
                      Agent
                    </Label>
                    <Select
                      value={editForm.tracking.agent_id || "none"}
                      onValueChange={(value) => updateTrackingField('agent_id', value === "none" ? '' : value, referenceData.agents, 'agent_name')}
                    >
                      <SelectTrigger data-testid="qr-edit-agent-select">
                        <SelectValue placeholder="Select agent" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {referenceData.agents.map(agent => (
                          <SelectItem key={agent.id} value={agent.id}>
                            {agent.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Partnership */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Handshake className="h-4 w-4 text-muted-foreground" />
                      Partnership
                    </Label>
                    <Select
                      value={editForm.tracking.partnership_id || "none"}
                      onValueChange={(value) => updateTrackingField('partnership_id', value === "none" ? '' : value, referenceData.partnerships, 'partnership_name')}
                    >
                      <SelectTrigger data-testid="qr-edit-partnership-select">
                        <SelectValue placeholder="Select partnership" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {referenceData.partnerships.map(partnership => (
                          <SelectItem key={partnership.id} value={partnership.id}>
                            {partnership.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Listing Reference */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Home className="h-4 w-4 text-muted-foreground" />
                    Listing Reference
                  </Label>
                  <Input
                    value={editForm.tracking.listing_ref}
                    onChange={(e) => setEditForm(prev => ({
                      ...prev,
                      tracking: { ...prev.tracking, listing_ref: e.target.value }
                    }))}
                    placeholder="e.g., PROP-2024-001"
                    data-testid="qr-edit-listing-ref-input"
                  />
                </div>
              </div>

              <Separator />

              {/* UTM Parameters */}
              <div className="space-y-4">
                <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">UTM Parameters</h4>
                <div className="space-y-2">
                  <Label htmlFor="edit-campaign">Campaign Name</Label>
                  <Input
                    id="edit-campaign"
                    value={editForm.utm.utm_campaign}
                    onChange={(e) => setEditForm(prev => ({
                      ...prev,
                      utm: { ...prev.utm, utm_campaign: e.target.value.toLowerCase().replace(/[^a-z0-9-_]/g, '-') }
                    }))}
                    placeholder="e.g., summer-2024"
                    data-testid="qr-edit-campaign-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Auto-included: utm_source=qr, utm_medium=offline
                  </p>
                </div>
              </div>
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={saving || !editForm.name.trim()} data-testid="qr-edit-save-button">
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PrintReadyQRDialog
        open={printDialogOpen}
        onOpenChange={setPrintDialogOpen}
        qrId={id}
        qrName={qrCode?.name}
        hasLogo={!!qrCode?.design?.image_options?.image_url}
      />
    </div>
  );
}
